import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import IconCancel from "@floji-internal/mui-icons/Cancel";

import { Strings } from "--root/strings/en";

import { Button } from "--floponents-web/atoms/Button";
import { Form } from "--floponents-web/atoms/Form";
import { IconButton } from "--components/buttons/iconButton";
import { SelectInput, tSelectValue } from "--floponents-web/atoms/SelectInput";
import SurfaceBody from "--floponents-web/atoms/Surface/SurfaceBody";
import { Tooltip } from "--components/tooltip";
import { DatePicker } from "--floponents-web/organisms/Date/DatePicker";
import { EmojiPickerButton } from "--floponents-web/organisms/EmojiPicker";

import { TextInput } from "--floponents-web/atoms/TextInput";
import { Body2 } from "--components/typography";

import { PresenceStatus } from "--client/../static_types/presence.types";
import { isSameDate } from "--root/utils/datetime";
import { EmojiBlock } from "--client/../static_types/slate/Shared";
import { useEmojiList } from "../../../common/hooks/useEmojiList";

// TODO: Apply this style to Form.ActionsGutter?
const StatusFormActionsGutter = styled(Form.ActionsGutter)`
    margin-top: ${props => props.theme.spacing(2)};
`;

const StatusFormStatusEntry = styled.div`
    align-items: center;
    display: flex;
    gap: ${props => props.theme.spacing(1)};
`;
const StatusFormStatusClearButton = styled(IconButton).attrs(() => ({ size: "small", color: "greyInverted", icon: <IconCancel /> }))`
    flex-shrink: 0;
`;

const StatusFormStatusTextInput = styled(TextInput)`
    width: 100%;
`;

const StatusFormExistingExpiryOption = styled.div`
    align-items: center;
    display: flex;
    gap: ${props => props.theme.spacing(1)};
`;

enum StatusExpiry {
    Never = 0,
    Custom = 1,
    Existing = 2,
    Today = 10,
    FiveMinutes = 11,
    TenMinutes = 12,
    FifteenMinutes = 13,
    ThirtyMinutes = 14,
    OneHour = 15,
}

const statusExpiryOptionNever = {
    key: StatusExpiry.Never,
    value: Strings.status.expiryTime.never
};

const statusExpiryOptionFiveMinutes = {
    key: StatusExpiry.FiveMinutes,
    value: Strings.status.expiryTime.fiveMinutes
};
const statusExpiryOptionTenMinutes = {
    key: StatusExpiry.TenMinutes,
    value: Strings.status.expiryTime.tenMinutes
};
const statusExpiryOptionFifteenMinutes = {
    key: StatusExpiry.FifteenMinutes,
    value: Strings.status.expiryTime.fifteenMinutes
};
const statusExpiryOptionThirtyMinutes = {
    key: StatusExpiry.ThirtyMinutes,
    value: Strings.status.expiryTime.thirtyMinutes
};
const statusExpiryOptionOneHour = {
    key: StatusExpiry.OneHour,
    value: Strings.status.expiryTime.oneHour
};
const statusExpiryOptionToday = {
    key: StatusExpiry.Today,
    value: Strings.status.expiryTime.today
};
const statusExpiryOptionCustom = {
    key: StatusExpiry.Custom,
    value: Strings.status.expiryTime.custom
};

const defaultStatusExpiryOptions: tSelectValue<StatusExpiry>[] = [
    statusExpiryOptionNever,
    statusExpiryOptionFiveMinutes,
    statusExpiryOptionTenMinutes,
    statusExpiryOptionFifteenMinutes,
    statusExpiryOptionThirtyMinutes,
    statusExpiryOptionOneHour,
    statusExpiryOptionToday,
    statusExpiryOptionCustom
];

export interface StatusFormProps {
    initialStatus?: PresenceStatus;
    isWorking?: boolean;
    onSubmit?: (status?: PresenceStatus) => void;
    onCancel?: () => void;
}
export const StatusForm = (props: StatusFormProps) => {
    const emojiList = useEmojiList();

    /** Gets flomoji details from emoji character. */
    const getEmojiByChar = useCallback((char: string) => {
        if (emojiList) {
            const matchingEmoji = Object.values(emojiList?.emojisByShortcut)
                .find(emoji => emoji.emoji === char);
            return matchingEmoji;
        } else return undefined
    }, [emojiList]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialStatus = useMemo(() => props.initialStatus, []);

    const initialStatusExpiry = useMemo(() => {
        if (initialStatus) {
            if (initialStatus.until === Number.MAX_SAFE_INTEGER) {
                return statusExpiryOptionNever;
            }

            const expiryDate = new Date(initialStatus.until);

            // Format with just time if expires today, otherwise include full date
            const formatter = new Intl.DateTimeFormat(
                navigator.languages as string[],
                isSameDate(expiryDate)
                    ? { timeStyle: "short" }
                    : { timeStyle: "short", dateStyle: "full" }
            );

            return {
                key: StatusExpiry.Existing,
                value: (
                    <StatusFormExistingExpiryOption>
                        {formatter.format(expiryDate)}
                        <Body2>{Strings.status.existingExpiryLabel}</Body2>
                    </StatusFormExistingExpiryOption>
                )
            };
        }
        return statusExpiryOptionToday;
    }, [initialStatus]);

    const statusExpiryOptions = useMemo(() => {
        // If existing non-default status expiry set
        return (
            initialStatus &&
            !defaultStatusExpiryOptions.includes(initialStatusExpiry)
        )
            ? [initialStatusExpiry, ...defaultStatusExpiryOptions]
            : defaultStatusExpiryOptions;
    }, [initialStatusExpiry, initialStatus]);

    const [statusText, setStatusText] = useState(initialStatus?.text ?? "");
    const [statusEmoji, setStatusEmoji] = useState(initialStatus?.emoji);
    const [statusExpiry, setStatusExpiry] =
        useState<tSelectValue<StatusExpiry>>(initialStatusExpiry);
    const [customExpiryDate, setCustomExpiryDate] = useState<Date>();

    const [isUserModified, setIsUserModified] = useState(false);

    // Use a min time of five minutes from when dialog was opened for date picker
    const minTime = useMemo(() => {
        const date = new Date();
        date.setMinutes(date.getMinutes() + 5);
        return date;
    }, []);

    useEffect(() => {
        setIsUserModified(
            // If status content has changed since form was shown
            JSON.stringify(initialStatus) !== JSON.stringify(props.initialStatus) ||
            // Or status values have do not match initial values
            (statusText !== (initialStatus?.text ?? "")) ||
            statusEmoji !== initialStatus?.emoji ||
            statusExpiry !== initialStatusExpiry
        );
    }, [
        initialStatusExpiry, initialStatus, initialStatus?.emoji,
        initialStatus?.text, statusEmoji, statusExpiry, statusText,
        props.initialStatus
    ]);

    const handleStatusEmojiChanged = useCallback((emoji: EmojiBlock) => {
        setStatusEmoji(emoji.children[0].text);
    }, []);
    const handleStatusTextChanged = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        setStatusText(ev.target.value);
    }, []);
    const handleStatusExpiryChanged = useCallback((expiry: tSelectValue<StatusExpiry>) => {
        setStatusExpiry(expiry);
    }, []);

    const handleStatusClear = useCallback(() => {
        setStatusText("");
        setStatusEmoji(undefined);
        setStatusExpiry(statusExpiryOptionToday);
        setCustomExpiryDate(undefined);
    }, []);

    const isCleared = !statusText && !statusEmoji;

    const handleSubmit = useCallback(() => {
        const now = new Date();
        let until: number;
        switch (statusExpiry.key) {
            case StatusExpiry.FiveMinutes:
                until = now.setMinutes(now.getMinutes() + 5);
                break;
            case StatusExpiry.TenMinutes:
                until = now.setMinutes(now.getMinutes() + 10);
                break;
            case StatusExpiry.FifteenMinutes:
                until = now.setMinutes(now.getMinutes() + 15);
                break;
            case StatusExpiry.ThirtyMinutes:
                until = now.setMinutes(now.getMinutes() + 30);
                break;
            case StatusExpiry.OneHour:
                until = now.setHours(now.getHours() + 1);
                break;
            case StatusExpiry.Today:
                now.setDate(now.getDate() + 1);
                until = now.setHours(0, 0, 0, 0);
                break;

            case StatusExpiry.Custom:
                if (customExpiryDate) {
                    until = customExpiryDate.getTime();
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case StatusExpiry.Existing:
                if (initialStatus) {
                    until = initialStatus.until;
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case StatusExpiry.Never:
            default:
                until = Number.MAX_SAFE_INTEGER;
        }

        if (!isCleared) {
            props.onSubmit?.({
                text: statusText ?? "",
                emoji: statusEmoji ?? "🪧",
                until
            });
        } else {
            props.onSubmit?.();
        }
    }, [
        customExpiryDate, initialStatus, isCleared, props, statusEmoji,
        statusExpiry.key, statusText
    ]);

    const emojiIcon = useMemo(() => {
        if (!statusEmoji) return;
        return getEmojiByChar(statusEmoji)
    }, [getEmojiByChar, statusEmoji]);
    return (
        <SurfaceBody>
            <Form.BasicForm>
                <StatusFormStatusEntry>
                    <StatusFormStatusTextInput
                        leadingComponent={
                            <EmojiPickerButton
                                emojiIcon={emojiIcon}
                                placement="right"
                                onSelected={handleStatusEmojiChanged}
                            />
                        }
                        trailingComponent={!isCleared
                            ? (
                                <Tooltip
                                    tip={Strings.status.clearStatus}
                                >
                                    <StatusFormStatusClearButton
                                        disabled={isCleared}
                                        onClick={handleStatusClear}
                                    />
                                </Tooltip>
                            )
                            : undefined}
                        placeholder={Strings.status.statusEntryPlaceholder}
                        value={statusText ?? ""}
                        onChange={handleStatusTextChanged}
                    />
                </StatusFormStatusEntry>
                {!isCleared && <>
                    <SelectInput
                        label={Strings.status.expiry}
                        value={statusExpiry}
                        options={statusExpiryOptions}
                        onChange={handleStatusExpiryChanged}
                    />
                    {statusExpiry.key === StatusExpiry.Custom && (
                        <DatePicker
                            onPick={setCustomExpiryDate}
                            minTime={minTime}
                            showTime
                        />
                    )}
                </>}
            </Form.BasicForm>
            <StatusFormActionsGutter>
                <Button
                    type="submit"
                    color="positive"
                    onClick={handleSubmit}
                    disabled={(!isUserModified) || props.isWorking}
                >
                    {Strings.save}
                </Button>
                <Button color="grey" onClick={props.onCancel}>
                    {Strings.cancel}
                </Button>
            </StatusFormActionsGutter>
        </SurfaceBody>
    );
};
